import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type RoutingAgentData = {
  tools: ("transferConversation" | "assignAgent")[];
  systemPrompt: string;
};

type StepAgentRoutingProps = {
  onSubmit: (data: RoutingAgentData) => Promise<void>;
  initialData: RoutingAgentData | null;
};

const availableTools = [
  { value: "transferConversation", label: "Transferir conversación" },
  { value: "assignAgent", label: "Asignar agente" },
];

const defaultTools: ("transferConversation" | "assignAgent")[] = [
  "transferConversation",
  "assignAgent",
];

const defaultSystemPrompt = `Eres un agente enrutador especializado en dirigir conversaciones hacia el agente o proyecto más apropiado.

Tu función principal es:
- Analizar la consulta del cliente
- Determinar el mejor agente o proyecto para manejar la conversación
- Transferir o asignar la conversación apropiadamente
- Proporcionar información inicial útil antes de la transferencia

Mantén las interacciones breves y enfócate en dirigir eficientemente a los clientes hacia la ayuda que necesitan.`;

export const StepAgentRouting = ({ onSubmit, initialData }: StepAgentRoutingProps) => {
  const [formData, setFormData] = useState<RoutingAgentData>({
    tools: defaultTools,
    systemPrompt: defaultSystemPrompt,
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleToolsChange = (checked: boolean, tool: string) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        tools: [...prev.tools, tool as any],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        tools: prev.tools.filter((t) => t !== tool),
      }));
    }
  };

  const handleSystemPromptChange = (systemPrompt: string) => {
    setFormData((prev) => ({
      ...prev,
      systemPrompt,
    }));
  };

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const isFormValid = () => {
    return formData.tools.length > 0 && formData.systemPrompt.trim() !== "";
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-lg font-semibold mb-4">Paso 2: Configurar agente enrutador</p>
      </div>

      <div>
        <Label className="font-semibold mb-4 block">Herramientas del agente enrutador</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Por defecto, el agente enrutador incluye las herramientas necesarias para transferir
          conversaciones y asignar agentes específicos.
        </p>

        <div className="grid grid-cols-2 gap-4">
          {availableTools.map((tool) => (
            <div key={tool.value} className="flex items-center gap-2">
              <Checkbox
                id={tool.value}
                checked={formData.tools.includes(tool.value as any)}
                onCheckedChange={(checked) => handleToolsChange(checked as boolean, tool.value)}
              />
              <Label htmlFor={tool.value} className="font-normal cursor-pointer">
                {tool.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="systemPrompt" className="font-semibold">
          System Prompt del Agente Enrutador
        </Label>
        <p className="text-sm text-muted-foreground mb-4">
          Define cómo el agente enrutador debe comportarse. Este prompt está optimizado para
          funciones de enrutamiento, pero puedes personalizarlo según tus necesidades.
        </p>
        <Textarea
          id="systemPrompt"
          value={formData.systemPrompt}
          onChange={(e) => handleSystemPromptChange(e.target.value)}
          placeholder="Escribe el system prompt del agente enrutador..."
          className="min-h-[300px] resize-y font-mono text-sm"
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!isFormValid()}>
          Continuar
        </Button>
      </div>
    </div>
  );
};
